import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Phone, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SEO } from "@/components/SEO";
import { PHONE, PHONE_LINK } from "@/lib/brand";

const EVENT_TYPES = ["Wedding / Bridal", "Engagement", "Reception", "Party / Occasion", "Photoshoot", "Festival", "Other"];

const INIT = {
  name: "", mobile: "", email: "", event_type: "", event_date: "", event_time: "",
  event_location: "", city: "", preferred_artist: "", service: "", num_people: 1,
  hairstyling: false, draping: false, requirements: "",
};

export default function Enquire() {
  const [params] = useSearchParams();
  const [form, setForm] = useState(INIT);
  const [artists, setArtists] = useState([]);
  const [services, setServices] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/artists").then((r) => {
      setArtists(r.data);
      const slug = params.get("artist");
      if (slug) {
        const found = r.data.find((a) => a.slug === slug);
        if (found) setForm((f) => ({ ...f, preferred_artist: found.name }));
      }
    }).catch(() => {});
    api.get("/services").then((r) => {
      setServices(r.data);
      const svc = params.get("service");
      if (svc) setForm((f) => ({ ...f, service: svc }));
    }).catch(() => {});
  }, [params]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const payload = { ...form, email: form.email || null, num_people: Number(form.num_people) || 1 };
      const { data } = await api.post("/enquiries", payload);
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (result)
    return (
      <div data-testid="enquiry-success-page" className="px-4 sm:px-6 max-w-2xl mx-auto py-16 sm:py-24 text-center">
        <SEO title="Enquiry Received" description="Thank you for contacting The Velvet Brush." path="/enquire" />
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
          className="bg-white rounded-[2rem] border border-[#EAE5DF] p-8 sm:p-12 shadow-xl">
          <CheckCircle2 className="w-14 h-14 text-velvet mx-auto" />
          <h1 className="mt-6 font-serif font-bold text-ink text-3xl sm:text-4xl">Thank you for contacting The Velvet Brush.</h1>
          <p className="mt-4 text-ink/65 leading-relaxed">
            Your enquiry has been received. Our team will contact you shortly to discuss availability, pricing and booking.
          </p>
          <div className="mt-6 inline-block bg-cream rounded-full px-6 py-3">
            <span className="text-xs uppercase tracking-widest text-ink/50">Enquiry Number</span>
            <p className="font-serif text-2xl text-ink font-semibold" data-testid="enquiry-number">{result.enquiry_number}</p>
          </div>
          <p className="mt-6 text-sm text-ink/55 italic">Payment is collected personally after booking confirmation.</p>
          <a href={PHONE_LINK} data-testid="success-call-link" className="mt-4 flex items-center justify-center gap-2 text-sm font-semibold text-velvet hover:text-velvethover">
            <Phone className="w-4 h-4" /> Call {PHONE} for further details
          </a>
          <Link to="/" className="mt-8 inline-block">
            <Button data-testid="back-home-btn" variant="outline" className="rounded-full border-ink/25 px-8">Back to Home</Button>
          </Link>
        </motion.div>
      </div>
    );

  const inputCls = "rounded-xl h-12 bg-white";
  const selectCls = "rounded-xl h-12 bg-white";

  return (
    <div data-testid="enquire-page" className="px-4 sm:px-6 lg:px-12 max-w-3xl mx-auto py-10 sm:py-16">
      <SEO
        title="Book / Enquire"
        description="Submit a booking enquiry with The Velvet Brush — no account needed. Our team will call you to confirm availability and pricing."
        path="/enquire"
      />
      <div className="text-center">
        <p className="text-xs uppercase tracking-[0.3em] font-semibold text-velvet">Book Your Look</p>
        <h1 className="mt-2 font-serif font-bold text-ink text-3xl sm:text-4xl lg:text-5xl">Book / Enquire</h1>
        <p className="mt-4 text-ink/60 text-sm sm:text-base">
          No account needed — fill this in under a minute and we'll call you back.
        </p>
      </div>

      <form onSubmit={submit} data-testid="enquiry-form" className="mt-10 bg-white rounded-[2rem] border border-[#EAE5DF] p-6 sm:p-10 shadow-sm grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="name">Name *</Label>
          <Input id="name" data-testid="enquiry-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" />
        </div>
        <div>
          <Label htmlFor="mobile">Mobile Number *</Label>
          <Input id="mobile" data-testid="enquiry-mobile" required type="tel" value={form.mobile} onChange={set("mobile")} className={inputCls} placeholder="10-digit mobile" />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" data-testid="enquiry-email" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="Optional" />
        </div>
        <div>
          <Label>Event Type *</Label>
          <Select required value={form.event_type} onValueChange={set("event_type")}>
            <SelectTrigger data-testid="enquiry-event-type" className={selectCls}><SelectValue placeholder="Select event type" /></SelectTrigger>
            <SelectContent>
              {EVENT_TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`event-type-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Makeup Service *</Label>
          <Select required value={form.service} onValueChange={set("service")}>
            <SelectTrigger data-testid="enquiry-service" className={selectCls}><SelectValue placeholder="Select service" /></SelectTrigger>
            <SelectContent>
              {services.map((s) => <SelectItem key={s.id} value={s.name} data-testid={`service-option-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="event_date">Event Date *</Label>
          <Input id="event_date" data-testid="enquiry-event-date" required type="date" value={form.event_date} onChange={set("event_date")} className={inputCls} />
        </div>
        <div>
          <Label htmlFor="event_time">Event Time</Label>
          <Input id="event_time" data-testid="enquiry-event-time" type="time" value={form.event_time} onChange={set("event_time")} className={inputCls} />
        </div>
        <div>
          <Label htmlFor="event_location">Event Location / Venue *</Label>
          <Input id="event_location" data-testid="enquiry-event-location" required value={form.event_location} onChange={set("event_location")} className={inputCls} placeholder="Venue or area" />
        </div>
        <div>
          <Label htmlFor="city">City *</Label>
          <Input id="city" data-testid="enquiry-city" required value={form.city} onChange={set("city")} className={inputCls} placeholder="e.g. Lucknow" />
        </div>
        <div>
          <Label>Preferred Artist</Label>
          <Select value={form.preferred_artist || "any"} onValueChange={(v) => setForm((f) => ({ ...f, preferred_artist: v === "any" ? "" : v }))}>
            <SelectTrigger data-testid="enquiry-preferred-artist" className={selectCls}><SelectValue placeholder="Any artist" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any" data-testid="artist-option-any">No preference</SelectItem>
              {artists.map((a) => <SelectItem key={a.id} value={a.name} data-testid={`artist-option-${a.slug}`}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="num_people">Number of People</Label>
          <Input id="num_people" data-testid="enquiry-num-people" type="number" min="1" max="50" value={form.num_people} onChange={set("num_people")} className={inputCls} />
        </div>
        <div className="sm:col-span-2 flex flex-col sm:flex-row gap-4">
          <label className="flex items-center gap-3 bg-cream rounded-xl px-4 py-3 cursor-pointer flex-1" data-testid="enquiry-hairstyling-label">
            <input type="checkbox" data-testid="enquiry-hairstyling" checked={form.hairstyling}
              onChange={(e) => setForm((f) => ({ ...f, hairstyling: e.target.checked }))}
              className="w-4 h-4 accent-[#E69AB8]" />
            <span className="text-sm text-ink">Hairstyling Required</span>
          </label>
          <label className="flex items-center gap-3 bg-cream rounded-xl px-4 py-3 cursor-pointer flex-1" data-testid="enquiry-draping-label">
            <input type="checkbox" data-testid="enquiry-draping" checked={form.draping}
              onChange={(e) => setForm((f) => ({ ...f, draping: e.target.checked }))}
              className="w-4 h-4 accent-[#E69AB8]" />
            <span className="text-sm text-ink">Draping Required</span>
          </label>
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="requirements">Additional Requirements</Label>
          <Textarea id="requirements" data-testid="enquiry-requirements" value={form.requirements} onChange={set("requirements")}
            className="rounded-xl bg-white min-h-[100px]" placeholder="Tell us anything else — outfit colours, theme, timings…" />
        </div>

        {error && <p data-testid="enquiry-error" className="sm:col-span-2 text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3">{error}</p>}

        <div className="sm:col-span-2">
          <Button type="submit" data-testid="submit-enquiry-btn" disabled={submitting}
            className="w-full rounded-full bg-velvet hover:bg-velvethover text-ink font-semibold h-14 text-base active:scale-95 transition-transform">
            {submitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting…</> : "Submit Enquiry"}
          </Button>
          <p className="mt-4 text-center text-xs text-ink/50">
            All bookings are subject to confirmation. Payment is collected personally after booking confirmation.
          </p>
          <a href={PHONE_LINK} data-testid="enquire-call-link" className="mt-2 flex items-center justify-center gap-2 text-sm font-semibold text-velvet hover:text-velvethover">
            <Phone className="w-4 h-4" /> Call {PHONE} for further details
          </a>
        </div>
      </form>
    </div>
  );
}
